import React, {useCallback, useEffect, useState} from "react";
import {Search, Item} from "../../../entrypoints/dashboard/layouts/dashboard/sidebar/icons/icons/icons";
import {useDispatch} from "react-redux";
import {doSearch} from "../../../services/common/actions";
import getCDNLink from "../../../constants/cdn-link";
import moment from "moment";
import {Link} from "react-router-dom";
import debounce from "lodash/debounce"
import config from "constants/config";
import {random} from "../../../entrypoints/dashboard/screens/comms/appeal";

import "./searchbar.scss";


const SearchIcon = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
        d="M10.4063 17.6C14.2722 17.6 17.4063 14.466 17.4063 10.6C17.4063 6.73398 14.2722 3.59998 10.4063 3.59998C6.54026 3.59998 3.40625 6.73398 3.40625 10.6C3.40625 14.466 6.54026 17.6 10.4063 17.6Z"
        stroke="#6A3CE2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20.6125 19.8L15.8125 15" stroke="#6A3CE2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
</svg>;


const SearchBar = () => {

    let [search, setSearch] = useState("");
    let [results, showResults] = useState(false);
    let [loading, setLoading] = useState(false);
    let [data, setData] = useState({});
    let [types, setTypes] = useState([]);
    let [placeholder, setPlaceholder] = useState("");
    let [current, setCurrent] = useState("");
    let dispatch = useDispatch();

    const onSearch = (s) => {

        setLoading(true);

        dispatch(doSearch(s))
            .then(res => {

                if (res.status === 200 && res.data && res.data.length) {

                    let d = res.data.reduce((map, i) => {
                        if (map[i.type]) {
                            map[i.type].push(i.object)
                            return map
                        } else {
                            map[i.type] = [];
                            map[i.type].push(i.object)
                            return map
                        }
                    }, {});

                    setData(d);
                    setTypes(Object.keys(d));

                } else {
                    setData({});
                    setTypes([]);
                }

            })
            .finally(() => setLoading(false))
    };

    const debounceFn = useCallback(debounce(onSearch, 1000), []);

    useEffect(() => {
        if (search.length < 2) {
            setData({});
            setTypes([]);
        } else {
            debounceFn(search);
        }
    }, [search]);

    useEffect(() => {

        const tips = ['Search any invoice...', 'Search any product...', 'Search...'];

        const storageKey = "searchbar-tip";
        const tipDelay = 80;
        const tipTimeout = 120;
        const eraseTimeout = 3000;

        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        const animateTip = async (tip, isLast) => {
            let currentTip = "";
            for (let i = 0; i < tip.length; i++) {
                currentTip += tip.charAt(i);
                setPlaceholder(currentTip);
                await delay(tipTimeout);
            }
            await delay(eraseTimeout);
            if (!isLast) {
                for (let i = tip.length; i > 0; i--) {
                    currentTip = currentTip.slice(0, -1);
                    setPlaceholder(currentTip);
                    await delay(tipDelay);
                }
            }
        };

        const showTips = async () => {
            for (let i = 0; i < tips.length; i++) {
                await animateTip(tips[i], i === tips.length - 1);
            }
            localStorage.setItem(storageKey, true);
        };

        const hasShownTips = localStorage.getItem(storageKey) === "true";

        if (!hasShownTips) {
            showTips();
        } else {
            setPlaceholder(tips[2]);
        }
    }, []);


    return <div className="header-searchbar" style={{flex: 3}} onMouseEnter={() => showResults(true)}
                onMouseLeave={() => showResults(false)}>

        <div className="header-searchbar-input">
            <Search/>
            <input
                placeholder={placeholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={(e) => e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)}
            />
        </div>

        <div
            className={`header-searchbar-container ${(search && search.length > 2) && !loading && !types.length && results ? "" : "d-none"}`}>
            <div className="header-searchbar-notfound">
                <div className="header-searchbar-notfound-icon"><SearchIcon/></div>
                <div className="header-searchbar-notfound-title">No matching search results</div>
                <div className="header-searchbar-notfound-description">Try again using more general search terms</div>
            </div>
        </div>

        <div className={`header-searchbar-container ${types.length && results && search && !loading ? "" : "d-none"}`}>
            <div className="search-header">
                <div className="search-categories">
                    <div className={!current ? "active" : ""} onClick={() => setCurrent("")}>All</div>
                    {types.map((type, key) => <div key={key} onClick={() => setCurrent(type)}
                                                   className={type === current ? "active" : ""}>{type.toLowerCase()}</div>)}
                </div>
            </div>

            <div className="search-body">
                {Object.keys(data).map(d => {

                    if (!current) {
                        if (d === "PRODUCT") {
                            return data[d].map(({cloudflare_image_id, title, uniqid}) => {

                                let image = <Item/>;
                                if (cloudflare_image_id) {
                                    image = <img src={getCDNLink({id: cloudflare_image_id, type: "shopItem"})} alt=""/>;
                                }

                                return <Link to={`/products/edit/${uniqid}`} key={uniqid} className="search-item">
                                    <div className="search-item-icon">
                                        {image}
                                    </div>
                                    <div>
                                        <span>{title}</span>
                                        <span>{uniqid}</span>
                                    </div>
                                </Link>
                            });
                        } else {
                            if (d === "INVOICE") {
                                return data[d].map((data) => {

                                    let {cloudflare_image_id, created_at, product_title, uniqid, gateway} = data;

                                    let image;
                                    if (cloudflare_image_id) {
                                        image =
                                            <img src={getCDNLink({id: cloudflare_image_id, type: "shopItem"})} alt=""/>;
                                    } else {
                                        image = gateway ? <img src={config.PAYMENT_ICONS[gateway]} alt=""/> : <Item/>;
                                    }

                                    return <Link to={`/invoices/${uniqid}`} key={uniqid} className="search-item">
                                        <div className="search-item-icon">
                                            {image}
                                        </div>
                                        <div>
                                            <span>{uniqid}</span>
                                            <span>{moment(created_at * 1000).format('DD, MMM YYYY HH:mm')} {product_title ? `- ${product_title}` : ""}</span>
                                        </div>
                                    </Link>
                                });
                            }
                        }
                    } else {
                        if (current === "PRODUCT" && d === "PRODUCT") {
                            return data[d].map(({cloudflare_image_id, title, uniqid}) => {

                                let image = <Item/>;
                                if (cloudflare_image_id) {
                                    image = <img src={getCDNLink({id: cloudflare_image_id, type: "shopItem"})} alt=""/>;
                                }

                                return <Link to={`/products/edit/${uniqid}`} className="search-item" key={uniqid}>
                                    <div className="search-item-icon">
                                        {image}
                                    </div>
                                    <div>
                                        <span>{title}</span>
                                        <span>{uniqid}</span>
                                    </div>
                                </Link>
                            });
                        }
                        if (current === "INVOICE" && d === "INVOICE") {
                            return data[d].map((data) => {

                                let {cloudflare_image_id, created_at, product_title, uniqid, gateway} = data;

                                let image;
                                if (cloudflare_image_id) {
                                    image = <img src={getCDNLink({id: cloudflare_image_id, type: "shopItem"})} alt=""/>;
                                } else {
                                    image = gateway ? <img src={config.PAYMENT_ICONS[gateway]} alt=""/> : <Item/>;
                                }

                                return <Link to={`/invoices/${uniqid}`} key={uniqid} className="search-item">
                                    <div className="search-item-icon">
                                        {image}
                                    </div>
                                    <div>
                                        <span>{uniqid}</span>
                                        <span>{moment(created_at * 1000).format('DD, MMM YYYY HH:mm')} {product_title ? `- ${product_title}` : ""}</span>
                                    </div>
                                </Link>
                            });
                        }
                    }

                })}
            </div>
        </div>

        <div className={`header-searchbar-container ${loading ? "" : "hide"}`}>
            <div className="search-header">
                <div className="search-categories">
                    <div>All</div>
                    {[1, 2].map((key) => <div key={key}>
                        <div className="skeleton skeleton2" style={{width: random(100, 150)}}/>
                    </div>)}
                </div>
            </div>

            <div className="search-body">
                {[1, 2, 3, 4].map((key) => {

                    let image = <Item/>;

                    return [1, 2, 3, 4].map((key2) => <a href="/" key={`${key}-${key2}`} className="search-item">
                        <div className="search-item-icon">
                            {image}
                        </div>
                        <div className="d-flex flex-column justify-content-around">
                            <span className="d-flex"><span className="skeleton skeleton4 small"
                                                           style={{width: random(100, 250)}}/></span>
                            <span><span className="skeleton skeleton2 small" style={{width: random(80, 170)}}/></span>
                        </div>
                    </a>)
                })}
            </div>
        </div>
    </div>
}


export default SearchBar;
